/** RFC 4180 CSV reader/writer with delimiter sniffing. Runs in the worker. */

const DELIMITERS = [',', '\t', ';', '|'];

export function splitLine(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"' && field === '') {
      inQuotes = true;
    } else if (ch === delimiter) {
      out.push(field);
      field = '';
    } else {
      field += ch;
    }
  }
  out.push(field);
  return out;
}

/**
 * Sniffs the delimiter from the first few lines by scoring how consistently
 * each candidate splits the header block into multiple columns.
 */
export function detectDelimiter(text: string): string {
  const cut = Math.min(text.length, 65536);
  let sample = text.slice(0, cut);
  const lastNewline = sample.lastIndexOf('\n');
  if (lastNewline > 0) sample = sample.slice(0, lastNewline);
  const lines = sample.split(/\r\n|\n|\r/).filter((line) => line.trim() !== '').slice(0, 6);
  if (!lines.length) return ',';

  let best = ',';
  let bestScore = -1;
  for (const delimiter of DELIMITERS) {
    const widths = lines.map((line) => splitLine(line, delimiter).length).filter((n) => n > 1);
    if (!widths.length) continue;
    const min = Math.min(...widths);
    const max = Math.max(...widths);
    const consistency = min / max;
    // Worst-case column count weighted by how stable the split is across lines.
    const score = min * consistency * (widths.length / lines.length);
    if (score > bestScore) {
      bestScore = score;
      best = delimiter;
    }
  }
  return best;
}

/** Parses CSV text into records. Handles quotes, embedded newlines, CRLF, BOM. */
export function parseCsv(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    pushField();
    // Skip fully blank lines; keep genuine single-blank-field rows.
    if (row.length > 1 || row[0] !== '') rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"' && field === '') {
      // A quote only opens a quoted field at the very start of a field;
      // quotes inside plain text stay literal ("He said "hi"" is unquoted).
      inQuotes = true;
    } else if (ch === delimiter) {
      pushField();
    } else if (ch === '\n') {
      pushRow();
    } else if (ch === '\r') {
      if (text[i + 1] === '\n') i++;
      pushRow();
    } else {
      field += ch;
    }
  }
  if (field !== '' || row.length > 0) pushRow();

  // Drop leading blank rows.
  while (rows.length && rows[0].every((cell) => cell.trim() === '')) rows.shift();
  return rows;
}

/** JSON.stringify-style escaping without the serialization overhead. */
export function csvEscape(value: string | null): string {
  if (value === null) return '';
  if (/[",\r\n]/.test(value) || value !== value.trim()) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function toCsv(headers: string[], rows: Array<Array<string | null>>): string {
  const lines = [headers.map(csvEscape).join(',')];
  for (const row of rows) lines.push(row.map(csvEscape).join(','));
  return lines.join('\n');
}
